import * as Yup from "yup";
import { validation } from "@/constants/validation.translation";
import constants from "@/libs/constant";

/**
 * Validation class for General Settings form
 * Contains validation schema for all general settings fields
 */
class SettingValidationClass {
  
  /**
   * General Settings validation schema
   * @param {function} t - Translation function
   * @returns {Yup.ObjectSchema} Validation schema for general settings
   */
  static generalSettingValidation(t = (key) => key) {
    const { settings } = validation;
    return Yup.object().shape({
      home_page_general_header_logo: Yup.mixed()
        .required(t(settings.header_logo_required))
        .test('fileType', t(settings.invalid_file_format), (value) => {
          if (!value) return true;
          if (typeof value === 'string') return true; // URL string
          return constants.validation.settings.supportedImageFormats.includes(value.type);
        }),
      home_page_general_email_logo: Yup.mixed()
        .required(t(settings.email_logo_required))
        .test('fileType', t(settings.invalid_file_format), (value) => {
          if (!value) return true;
          if (typeof value === 'string') return true; // URL string
          return constants.validation.settings.supportedImageFormats.includes(value.type);
        }),
      home_page_general_favicon_logo: Yup.mixed()
        .required(t(settings.favicon_logo_required))
        .test('fileType', t(settings.invalid_file_format), (value) => {
          if (!value) return true;
          if (typeof value === 'string') return true; // URL string
          return constants.validation.settings.supportedImageFormats.includes(value.type);
        }),
      home_page_general_login_page_logo: Yup.mixed()
        .required(t(settings.login_page_logo_required))
        .test('fileType', t(settings.invalid_file_format), (value) => {
          if (!value) return true;
          if (typeof value === 'string') return true; // URL string
          return constants.validation.settings.supportedImageFormats.includes(value.type);
        }),
      setting_ios_app_version: Yup.string()
        .required(t(settings.ios_version_required))
        .max(constants.validation.settings.maxVersionLength, t(settings.version_max_length))
        .matches(constants.validation.settings.versionRegex, t(settings.version_numbers_only)),
      setting_android_app_version: Yup.string()
        .required(t(settings.android_version_required))
        .max(constants.validation.settings.maxVersionLength, t(settings.version_max_length))
        .matches(constants.validation.settings.versionRegex, t(settings.version_numbers_only)),
      admin_contact_email: Yup.string()
        .email(t(settings.invalid_email_format))
        .required(t(settings.contact_email_required)),
    });
  }

  /**
   * Admin Rewards Settings validation schema (the admin-grant months + auto-grant-on-signup
   * toggle, now its own subtab under Rewards Settings - separate from General Settings).
   * @param {function} t - Translation function
   * @returns {Yup.ObjectSchema} Validation schema for admin rewards settings
   */
  static adminRewardsSettingValidation(t = (key) => key) {
    const { settings } = validation;
    return Yup.object().shape({
      [constants.formFields.adminRewardsSettings.premiumGrantMonths]: Yup.number()
        .typeError(t(settings.premium_grant_months_invalid))
        .integer(t(settings.premium_grant_months_integer))
        .min(constants.validation.adminRewardsSettings.premiumGrantMonthsMin, t(settings.premium_grant_months_min))
        .required(t(settings.premium_grant_months_required)),
      // Backstop for the cross-field guard already enforced in the toggle's onChange handler:
      // rejects a submitted form where auto-grant is on but months is missing/invalid.
      [constants.formFields.adminRewardsSettings.premiumAutoGrantOnSignup]: Yup.boolean().test(
        "premium-auto-grant-requires-valid-months",
        t(settings.premium_auto_grant_invalid_combo),
        function (value) {
          if (!value) return true;
          const months = this.parent[constants.formFields.adminRewardsSettings.premiumGrantMonths];
          const numericMonths = Number(months);
          return (
            months !== "" &&
            months !== null &&
            months !== undefined &&
            Number.isInteger(numericMonths) &&
            numericMonths >= constants.validation.adminRewardsSettings.premiumGrantMonthsMin
          );
        }
      ),
    });
  }

  /**
   * Referral & Rewards Settings validation schema
   * @param {function} t - Translation function
   * @returns {Yup.ObjectSchema} Validation schema for referral & rewards settings
   */
  static referralSettingValidation(t = (key) => key) {
    const { referralSettings } = validation;
    const bounds = constants.validation.referralSettings;
    return Yup.object().shape({
      [constants.formFields.referralSettings.milestoneSize]: Yup.number()
        .typeError(t(referralSettings.milestone_size_invalid))
        .integer(t(referralSettings.milestone_size_integer))
        .min(bounds.milestoneSizeMin, t(referralSettings.milestone_size_min))
        .max(bounds.milestoneSizeMax, t(referralSettings.milestone_size_max))
        .required(t(referralSettings.milestone_size_required)),
      [constants.formFields.referralSettings.monthsPerMilestone]: Yup.number()
        .typeError(t(referralSettings.months_per_milestone_invalid))
        .integer(t(referralSettings.months_per_milestone_integer))
        .min(bounds.monthsPerMilestoneMin, t(referralSettings.months_per_milestone_min))
        .max(bounds.monthsPerMilestoneMax, t(referralSettings.months_per_milestone_max))
        .required(t(referralSettings.months_per_milestone_required)),
      [constants.formFields.referralSettings.consecutiveDaysRequired]: Yup.number()
        .typeError(t(referralSettings.consecutive_days_invalid))
        .integer(t(referralSettings.consecutive_days_integer))
        .min(bounds.consecutiveDaysMin, t(referralSettings.consecutive_days_min))
        // MUST stay <= 28 - the backend tracks the login streak as a bitmask on a 32-bit
        // Int column (Referral.loginStreakBitmask); a larger value would overflow it.
        .max(bounds.consecutiveDaysMax, t(referralSettings.consecutive_days_max))
        .required(t(referralSettings.consecutive_days_required)),
      [constants.formFields.referralSettings.minMessagesRequired]: Yup.number()
        .typeError(t(referralSettings.min_messages_invalid))
        .integer(t(referralSettings.min_messages_integer))
        .min(bounds.minMessagesMin, t(referralSettings.min_messages_min))
        .max(bounds.minMessagesMax, t(referralSettings.min_messages_max))
        .required(t(referralSettings.min_messages_required)),
    });
  }
}

export default SettingValidationClass;
